import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const defaultExteriorTrimSettings = {
  type: "EXTERIOR_TRIM_SETTINGS",
  enableOutsideCornerMoulding: true,
  enableInsideCornerMoulding: false,
  wallRoofBaseBoard: "NONE",
  wallRoofBaseBoardFilter: "ALL_EDGES",
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_151: DocMigration = {
  maxSchemaVersion: "0.0.151",
  description: "migrate view notes from porch settings and view note settings to exterior trim",
  apply(json: any) {
    for (const setId in json.settingsSets) {
      let porchSettingId: string | undefined = undefined;
      let porchViewNote: any = undefined;
      let rearPorchSettingId: string | undefined = undefined;
      let rearPorchViewNote: any = undefined;
      let viewNoteSettingId: string | undefined = undefined;
      let cornerBoardViewNote: any = undefined;
      let insideCornerBoardViewNote: any = undefined;
      let exteriorTrimSettingId: string | undefined = undefined;

      for (const settingId in json.settings) {
        const setting = json.settings[settingId];

        //view note from porch settings
        if (isOfSettingSet(setting, setId) && isPorchSetting(setting)) {
          porchSettingId = settingId;

          //check if that porch setting has a porch header trim view note
          if (setting.viewNotes?.PORCH_HEADER_TRIM) {
            porchViewNote = setting.viewNotes?.PORCH_HEADER_TRIM;
          }
        }
        //view note from rear porch settings
        if (isOfSettingSet(setting, setId) && isRearPorchSetting(setting)) {
          rearPorchSettingId = settingId;

          //check if that porch setting has a porch header trim view note
          if (setting.viewNotes?.PORCH_HEADER_TRIM) {
            rearPorchViewNote = setting.viewNotes?.PORCH_HEADER_TRIM;
          }
        }

        //view note from view note settings
        if (isOfSettingSet(setting, setId) && isViewNoteSetting(setting)) {
          viewNoteSettingId = settingId;

          //get cornerboard view note, enabled field should be enable
          if (setting.CORNERBOARD) {
            cornerBoardViewNote = {
              text: setting.CORNERBOARD?.text,
              tagOnlyOne: setting.CORNERBOARD.tagOnlyOne,
              enable: setting.CORNERBOARD.enabled,
            };

            insideCornerBoardViewNote = {
              text: setting.CORNERBOARD?.text,
              tagOnlyOne: setting.CORNERBOARD.tagOnlyOne,
              enable: false,
            };
          }
        }

        //search for exterior trim settings in this settings set
        if (isOfSettingSet(setting, setId) && isExteriorTrimSetting(setting)) {
          exteriorTrimSettingId = settingId;
        }
      }

      //assemble notes to be migrated to exterior trim view notes and delete original
      const migratedViewNotes = {};
      let newNote = false;
      if (rearPorchSettingId !== undefined && rearPorchViewNote !== undefined) {
        migratedViewNotes["PORCH_HEADER_TRIM"] = rearPorchViewNote;
        newNote = true;

        delete json.settings[rearPorchSettingId].viewNotes.PORCH_HEADER_TRIM;
      }
      if (porchSettingId !== undefined && porchViewNote !== undefined) {
        migratedViewNotes["PORCH_HEADER_TRIM"] = porchViewNote;
        newNote = true;

        delete json.settings[porchSettingId].viewNotes.PORCH_HEADER_TRIM;
      }
      if (viewNoteSettingId !== undefined && cornerBoardViewNote !== undefined) {
        migratedViewNotes["OUTSIDE_CORNER"] = cornerBoardViewNote;
        if (cornerBoardViewNote.tagOnlyOne === true) {
          migratedViewNotes["INSIDE_CORNER"] = insideCornerBoardViewNote;
        } else {
          migratedViewNotes["INSIDE_CORNER"] = cornerBoardViewNote;
        }

        newNote = true;

        delete json.settings[viewNoteSettingId].CORNERBOARD;
      }

      if (newNote === true) {
        //if exterior trim setting, add view note to it
        if (exteriorTrimSettingId) {
          const exteriorTrimSetting = json.settings[exteriorTrimSettingId];
          json.settings[exteriorTrimSettingId] = {
            ...exteriorTrimSetting,
            viewNotes: { ...migratedViewNotes },
          };
        }

        //if not, create one and add the note to it
        else {
          const newSettingsId = uid();
          (json.settings[newSettingsId] as any) = {
            ...{
              ...defaultExteriorTrimSettings,
              viewNotes: { ...migratedViewNotes },
            },
            entityType: "SETTINGS",
            id: newSettingsId,
            parentId: setId,
            sortIndex: 0,
          };
        }
      }
    }

    return json;
  },
};

export default migration_0_0_151;

function isOfSettingSet(setting: any, settingSetId: string): boolean {
  return setting.parentId === settingSetId;
}

function isPorchSetting(setting: any): boolean {
  return setting?.type === "PORCH_SETTINGS";
}

function isRearPorchSetting(setting: any): boolean {
  return setting?.type === "REAR_PORCH_SETTINGS";
}

function isViewNoteSetting(setting: any): boolean {
  return setting?.type === "VIEW_NOTE_SETTINGS";
}

function isExteriorTrimSetting(setting: any): boolean {
  return setting?.type === "EXTERIOR_TRIM_SETTINGS";
}
